import type { SalesByDayRow } from '@tailoring-crm/shared';

/**
 * The Home dashboard's sales figures, cut out of one sales report.
 *
 * Home asks the API for ONE report — every shop day from the first of the month five months
 * back up to today — and every sales panel on the screen is derived from its `byDay` rows here.
 * Six requests for six months, plus two more for the daily comparison, would be eight round
 * trips on a phone in a market for figures that are all sums of the same rows; and eight
 * answers computed at eight slightly different moments could disagree with each other, where
 * cuts of one answer cannot.
 *
 * Everything in this file is pure string and integer arithmetic on shop days (`YYYY-MM-DD`,
 * already in the shop's timezone because the server bucketed them so). Nothing here builds a
 * `Date` from a day, so nothing here can drift a day under the phone's own timezone.
 */

/** How many calendar months the Home sales panels cover, the current one included. */
export const MONTHS_SHOWN = 6;

/** A calendar month as `YYYY-MM`. */
export type Month = string;

function monthOf(day: string): Month {
  return day.slice(0, 7);
}

function dayOfMonth(day: string): number {
  return Number(day.slice(8, 10));
}

/** The month `offset` months after `month` (negative goes back), as `YYYY-MM`. */
export function shiftMonth(month: Month, offset: number): Month {
  const [year, monthNumber] = month.split('-').map(Number) as [number, number];
  const index = year * 12 + (monthNumber - 1) + offset;
  const shiftedYear = Math.floor(index / 12);
  const shiftedMonth = (index % 12) + 1;
  return `${String(shiftedYear).padStart(4, '0')}-${String(shiftedMonth).padStart(2, '0')}`;
}

/**
 * How many days a calendar month has. Gregorian rules spelled out rather than read off a
 * `Date`, for the reason at the top of the file.
 */
export function daysInMonth(month: Month): number {
  const [year, monthNumber] = month.split('-').map(Number) as [number, number];
  if (monthNumber === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(monthNumber) ? 30 : 31;
}

/**
 * The days Home asks the sales report for: the first of the month `MONTHS_SHOWN - 1` months
 * back, through today. Sent as a `custom` range, because here the days ARE the intent.
 */
export function salesWindow(today: string): { from: string; to: string } {
  return { from: `${shiftMonth(monthOf(today), -(MONTHS_SHOWN - 1))}-01`, to: today };
}

interface Totals {
  revenuePaise: number;
  orderCount: number;
}

function sumDays(rows: readonly SalesByDayRow[], keep: (day: string) => boolean): Totals {
  let revenuePaise = 0;
  let orderCount = 0;
  for (const row of rows) {
    if (!keep(row.day)) continue;
    revenuePaise += row.revenuePaise;
    orderCount += row.orderCount;
  }
  return { revenuePaise, orderCount };
}

export interface MonthRow extends Totals {
  month: Month;
  /** True for the month `today` falls in — its figures are so far, not final. */
  partial: boolean;
  /**
   * What this row's change is measured against, or `null` when the report does not reach back
   * far enough to know (the oldest month shown). For a finished month that is the whole month
   * before; for the month in progress it is the same days of the month before — see
   * `monthToDate` for why.
   */
  previousRevenuePaise: number | null;
}

/**
 * One row per month, oldest first, `MONTHS_SHOWN` of them ending at the month of `today`.
 * A month with no sales is a row of zeroes rather than a missing row — a gap in a bar chart
 * reads as "no data", and a quiet month is data.
 */
export function monthlyTotals(rows: readonly SalesByDayRow[], today: string): MonthRow[] {
  const current = monthOf(today);
  const months = Array.from({ length: MONTHS_SHOWN }, (_, index) =>
    shiftMonth(current, index - (MONTHS_SHOWN - 1)),
  );
  const totals = new Map(months.map((month) => [month, { revenuePaise: 0, orderCount: 0 }]));
  for (const row of rows) {
    const bucket = totals.get(monthOf(row.day));
    if (!bucket) continue;
    bucket.revenuePaise += row.revenuePaise;
    bucket.orderCount += row.orderCount;
  }

  const sameDays = monthToDate(rows, today);
  return months.map((month, index) => {
    const own = totals.get(month)!;
    const partial = month === current;
    const before = index === 0 ? null : totals.get(months[index - 1]!)!.revenuePaise;
    return {
      month,
      ...own,
      partial,
      previousRevenuePaise: partial ? sameDays.previous.revenuePaise : before,
    };
  });
}

export interface PeriodFigures extends Totals {
  /** Revenue over orders, rounded once at the end, as the sales report itself does. */
  averageOrderPaise: number;
}

function withAverage(totals: Totals): PeriodFigures {
  return {
    ...totals,
    averageOrderPaise:
      totals.orderCount === 0 ? 0 : Math.round(totals.revenuePaise / totals.orderCount),
  };
}

/**
 * This month so far, against the SAME days of last month.
 *
 * Not against the whole of last month: on the 5th, five days of sales against thirty-one would
 * read as an 80% collapse every single month, and an indicator that is red every morning until
 * the 25th is an indicator nobody looks at. Last month is cut at today's day-of-month (or at its
 * own last day, when today is the 31st and last month had 30).
 */
export function monthToDate(
  rows: readonly SalesByDayRow[],
  today: string,
): { current: PeriodFigures; previous: PeriodFigures } {
  const current = monthOf(today);
  const previous = shiftMonth(current, -1);
  const cutoff = dayOfMonth(today);
  return {
    current: withAverage(sumDays(rows, (day) => monthOf(day) === current)),
    previous: withAverage(
      sumDays(rows, (day) => monthOf(day) === previous && dayOfMonth(day) <= cutoff),
    ),
  };
}

export interface DailySeries {
  /** Revenue per day of this month, index 0 = the 1st, through today only. */
  thisMonth: number[];
  /** Revenue per day of last month, the whole month. */
  lastMonth: number[];
  /** How many day positions the x-axis needs: the longer of the two months. */
  axisDays: number;
  thisMonthKey: Month;
  lastMonthKey: Month;
}

/**
 * Day-by-day revenue for this month and last, lined up by day-of-month so the 12th sits over
 * the 12th. Days the report has no row for are zero rather than skipped, for the same reason
 * the server fills them.
 */
export function dailySeries(rows: readonly SalesByDayRow[], today: string): DailySeries {
  const thisMonthKey = monthOf(today);
  const lastMonthKey = shiftMonth(thisMonthKey, -1);
  const thisMonth = Array.from({ length: dayOfMonth(today) }, () => 0);
  const lastMonth = Array.from({ length: daysInMonth(lastMonthKey) }, () => 0);

  for (const row of rows) {
    const month = monthOf(row.day);
    const index = dayOfMonth(row.day) - 1;
    if (month === thisMonthKey && index < thisMonth.length) thisMonth[index]! += row.revenuePaise;
    else if (month === lastMonthKey) lastMonth[index]! += row.revenuePaise;
  }

  return {
    thisMonth,
    lastMonth,
    axisDays: Math.max(daysInMonth(thisMonthKey), lastMonth.length),
    thisMonthKey,
    lastMonthKey,
  };
}

/**
 * The change from `previous` to `current` as a ratio (`0.12` is up 12%), or `null` when there
 * is nothing to compare against. A rise from zero is not "up ∞%"; it is no comparison at all.
 */
export function percentChange(current: number, previous: number | null): number | null {
  if (previous === null || previous === 0) return null;
  return (current - previous) / previous;
}
